<template>
  <form @submit.prevent="signUp">
    <FormGroup>
      <View direction="column" gap="sm">
        <Text variant="h1">Welcome to Tallo</Text>
        <Text variant="body-large" color="secondary">Create an account to unlock the power of AI-driven leasing</Text>
      </View>

      <FormField label="Full Name" :error="form.fieldErrors('fullName')">
        <Input v-model="form.fields.fullName" placeholder="Type your full name" name="fullName" />
      </FormField>

      <FormField label="Email" :error="form.fieldErrors('email')">
        <Input v-model="form.fields.email" placeholder="Type your email" type="email" name="email" />
      </FormField>

      <View direction="column" gap="sm">
        <FormField class="password-form-field" label="Password" :error="form.fieldErrors('password')">
          <Input
            v-model="form.fields.password"
            placeholder="Create a password"
            type="password"
            name="password"
            @focus="showPasswordStrength = true"
          />
        </FormField>

        <PasswordStrength
          :show-component="showPasswordStrength"
          @password-status-change="setPasswordValidity"
          :password="form.fields.password"
        />
      </View>

      <Button type="submit" :loading="form.submitting">Create account</Button>

      <TermsNotice />

      <FormSeparator />
      <InvestorGoogleSignInButton :flow="'sign-up'" />

      <Text variant="body-large" align="center">
        <span>Have an account?&nbsp;</span>
        <Link :to="signInRoute()" :color="'accent'">Log in</Link>
      </Text>
    </FormGroup>
  </form>
</template>

<script setup lang="ts">
import { email, required } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { ref } from 'vue';

import { useAuthStore } from '@tallo/auth';
import {
  Button,
  FormField,
  FormGroup,
  Input,
  Link,
  PasswordStrength,
  Text,
  View,
  FormSeparator,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { signInRoute, useNavigation } from '@tallo/investor/navigation';

import { fullNameValidator } from './full-name.validator';
import InvestorGoogleSignInButton from './investor-google-sign-in-button.vue';
import TermsNotice from './terms-notice.vue';

const authStore = useAuthStore();
const { openEmailConfirmation } = useNavigation();

const showPasswordStrength = ref(false);
const passwordValid = ref(false);
const setPasswordValidity = (valid: boolean) => {
  passwordValid.value = valid;
};

const alerts = useAlertsStore();

const form = useForm(
  {
    fullName: '',
    email: '',
    password: '',
  },
  {
    fullName: {
      required,
      fullName: fullNameValidator,
    },
    email: { required, email },
    password: {
      required,
      strong: () => passwordValid.value,
    },
  },
);

async function signUp() {
  form.submit(async () => {
    try {
      const data = await authStore.investorSignUpWithCreds({
        name: form.fields.fullName,
        email: form.fields.email,
        password: form.fields.password,
      });

      openEmailConfirmation({ userId: data.userId, email: form.fields.email });
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        alerts.error('User already exists. Please attempt to log in using this email.');
      } else {
        alerts.error('Something went wrong');
      }
    }
  });
}
</script>
